import React, { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useDiscovery } from "./DiscoveryContext";
import {
  XMarkIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
} from "@heroicons/react/24/outline";

const DiscoveryWizard = ({ isOpen, onClose }) => {
  const { preferences, setDiscoveryStep } = useDiscovery();
  const [currentStep, setCurrentStep] = useState(preferences?.discoveryStep || 1);

  // Sync internal step with preferences if they change externally
  useEffect(() => {
    if (preferences?.discoveryStep > 0 && preferences?.discoveryStep < 4) {
      setCurrentStep(preferences.discoveryStep);
    }
  }, [preferences?.discoveryStep]);

  const steps = [
    {
      id: 1,
      title: "Welcome to InstaShop! 🎉",
      description:
        "Learn how to turn comments into sales with automated product DMs. We will show you the core workflow to get your first sale.",
      icon: "🎮",
    },
    {
      id: 2,
      title: "Configure Auto DMs",
      description:
        "Visit the 'Auto DM for Comments Bot' section to link products to your Instagram posts. We'll guide you through setting up response templates that automate your sales workflow.",
      icon: "🤖",
      action: "Find a post on your dashboard and click the 'Configure' button",
    },
    {
      id: 3,
      title: "Almost there! 🎯",
      description:
        "1. Switch to 'Button Template'\n2. Click 'Choose Product'\n3. Select a product\n\nThat's it! You're ready to sell! 🚀",
      icon: "✨",
    },
  ];

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
      setDiscoveryStep(currentStep + 1);
    } else {
      setDiscoveryStep(4); // Completed
      onClose();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    setDiscoveryStep(4); // Mark as completed/skipped
    onClose();
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-3xl bg-white dark:bg-gray-800 px-8 pb-8 pt-10 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg border border-gray-100 dark:border-gray-700">
                <div className="absolute right-6 top-6">
                  <button
                    onClick={onClose}
                    className="rounded-full p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div>
                  <div className="flex justify-center gap-2 mb-8">
                    {[1, 2, 3].map((step) => (
                      <div
                        key={step}
                        className={`h-1.5 w-12 rounded-full transition-all duration-500 ${
                          step <= currentStep
                            ? "bg-gradient-to-r from-purple-500 to-pink-500"
                            : "bg-gray-200 dark:bg-gray-700"
                        }`}
                      />
                    ))}
                  </div>

                  <div className="text-center">
                    <div className="text-6xl mb-6">
                      {steps[currentStep - 1].icon}
                    </div>
                    <Dialog.Title
                      as="h3"
                      className="text-2xl font-bold leading-6 text-gray-900 dark:text-white mb-4"
                    >
                      {steps[currentStep - 1].title}
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-gray-600 dark:text-gray-300 whitespace-pre-line leading-relaxed">
                        {steps[currentStep - 1].description}
                      </p>
                    </div>
                    {steps[currentStep - 1].action && (
                      <div className="mt-6 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-100 dark:border-purple-800">
                        <p className="text-purple-700 dark:text-purple-300 font-semibold text-sm">
                          {steps[currentStep - 1].action}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-10 flex flex-col-reverse sm:flex-row justify-between items-center gap-4">
                  <button
                    type="button"
                    className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 font-semibold text-sm transition-colors"
                    onClick={handleSkip}
                  >
                    Skip onboarding
                  </button>
                  <div className="flex gap-3 w-full sm:w-auto">
                    {currentStep > 1 && (
                      <button
                        type="button"
                        className="flex-1 sm:flex-none inline-flex justify-center items-center rounded-xl bg-gray-100 dark:bg-gray-700 px-6 py-3 text-sm font-bold text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600 transition-all font-sans"
                        onClick={handlePrevious}
                      >
                        <ChevronLeftIcon className="h-4 w-4 mr-2" />
                        Previous
                      </button>
                    )}
                    <button
                      type="button"
                      className="flex-1 sm:flex-none inline-flex justify-center items-center rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 px-8 py-3 text-sm font-bold text-white shadow-lg shadow-purple-200 dark:shadow-none hover:opacity-90 transition-all transform hover:scale-[1.02] active:scale-95 font-sans"
                      onClick={handleNext}
                    >
                      {currentStep === 3 ? "Got it! 🚀" : "Next"}
                      {currentStep < 3 && (
                        <ChevronRightIcon className="h-4 w-4 ml-2" />
                      )}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default DiscoveryWizard;
